/**
 * TON amount formatting + conversion.
 *
 * Money is *always* bigint nanotons inside the app. These helpers are the
 * ONLY place where we accept/return strings (for display, for JSON
 * transport, or for user input). Anything else must preserve bigint.
 *
 * 1 TON = 10^9 nanotons.
 */

/** 1 TON in nanotons. */
export const NANO_PER_TON = 1_000_000_000n;

/**
 * Convert a bigint nanoton amount to a human-readable TON string.
 *
 * @param nano   — the amount in nanotons
 * @param digits — number of fractional digits to preserve (default 4).
 *                 Set to 0 to trim to integer TON.
 *
 * Rounds half-to-even via a truncate-then-round strategy. We never let
 * rounding widen the amount because that would overstate funds.
 */
export function nanoToTon(nano: bigint, digits = 4): string {
  if (digits < 0 || digits > 9) {
    throw new RangeError('digits must be in [0, 9]');
  }

  const negative = nano < 0n;
  const abs = negative ? -nano : nano;
  const whole = abs / NANO_PER_TON;
  const frac = abs % NANO_PER_TON;

  if (digits === 0) {
    const wholeWithRound =
      frac * 2n >= NANO_PER_TON ? whole /* we intentionally do NOT round up */ : whole;
    return `${negative ? '-' : ''}${wholeWithRound.toString()}`;
  }

  // Build fractional part with full 9 digits, then slice to `digits`.
  const fracStr = frac.toString().padStart(9, '0').slice(0, digits);
  // Strip trailing zeros for a cleaner display, but keep at least 1 digit.
  const trimmed = fracStr.replace(/0+$/, '');
  const shown = trimmed.length === 0 ? '0' : trimmed;
  return `${negative ? '-' : ''}${whole.toString()}.${shown}`;
}

/**
 * Parse a user-entered TON string into bigint nanotons.
 *
 * Accepts values like `"1"`, `"1.5"`, `"0.000000001"`. Rejects anything
 * else with an `Error`. We do NOT use `parseFloat` anywhere on the money
 * path — the force-bigint hook would block it and we'd lose precision.
 */
export function tonToNano(input: string): bigint {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    throw new Error('Empty TON amount');
  }
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
    throw new Error(`Invalid TON amount: ${input}`);
  }

  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [wholePart, fracPart = ''] = unsigned.split('.');
  if (fracPart.length > 9) {
    throw new Error(`Too many fractional digits (max 9): ${input}`);
  }

  const fracPadded = fracPart.padEnd(9, '0');
  const nano = BigInt(wholePart) * NANO_PER_TON + BigInt(fracPadded);
  return negative ? -nano : nano;
}

/**
 * Display wrapper that appends the currency suffix. Used in badges and
 * headings. For detail views prefer composing `nanoToTon` + custom layout.
 */
export function formatTonAmount(
  nano: bigint,
  opts: { digits?: number; currency?: 'TON' | 'USDT' } = {},
): string {
  const { digits = 4, currency = 'TON' } = opts;
  return `${nanoToTon(nano, digits)} ${currency}`;
}

/**
 * Shorten a TON address for display: `EQAB..c5de`. Does NOT validate the
 * address — validation is a backend responsibility.
 */
export function shortAddress(address: string, head = 4, tail = 4): string {
  if (address.length <= head + tail + 2) return address;
  return `${address.slice(0, head)}..${address.slice(-tail)}`;
}
